import type { DecisionCategory, DecisionStatus } from "../api";

/**
 * Display labels and badge colors for decisions (FLOW-569/FLOW-572). Shared
 * by `DecisionsSection` (work item detail) and `DecisionsPage` so category
 * and status render identically wherever a decision shows up.
 */

const CATEGORY_LABELS: Record<DecisionCategory, string> = {
  architecture: "Architecture",
  infrastructure: "Infrastructure",
  convention: "Convention",
  dependency: "Dependency",
  security: "Security",
  performance: "Performance",
  process: "Process",
};

const STATUS_LABELS: Record<DecisionStatus, string> = {
  proposed: "Proposed",
  active: "Active",
  under_review: "Under review",
  superseded: "Superseded",
  invalidated: "Invalidated",
};

const STATUS_COLORS: Record<DecisionStatus, string> = {
  proposed: "bg-sunken text-ink-2 border-line",
  active: "bg-signal-green-tint text-signal-green border-signal-green/25",
  under_review: "bg-signal-amber-tint text-signal-amber border-signal-amber/25",
  superseded: "bg-sunken text-ink-3 border-line",
  invalidated: "bg-signal-red-tint text-signal-red border-signal-red/25",
};

/**
 * `Decision.category` is plain text on the backend (a CHECK constraint, not
 * a Postgres enum), so an unrecognized value falls back to the raw string
 * rather than throwing.
 */
export function decisionCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category as DecisionCategory] ?? category;
}

export function decisionStatusLabel(status: DecisionStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function decisionStatusColor(status: DecisionStatus): string {
  return STATUS_COLORS[status] ?? "bg-sunken text-ink-2 border-line";
}

export const DECISION_CATEGORY_BADGE_COLOR = "bg-sunken text-ink-2 border-line";

export const DECISION_CATEGORY_OPTIONS: {
  value: DecisionCategory;
  label: string;
}[] = (Object.keys(CATEGORY_LABELS) as DecisionCategory[]).map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

export const DECISION_STATUS_OPTIONS: {
  value: DecisionStatus;
  label: string;
}[] = (Object.keys(STATUS_LABELS) as DecisionStatus[]).map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));
